import {
  AwardPointsOrVouchersInput,
  AwardType,
  DirtyFieldsType,
  GetConfigureFormOperations,
  Operation,
} from '@occc-app/offerHelpers/types';
import { getOfferCustomDataOperation } from '@occc-app/offerHelpers/operations/getOfferCustomDataOperation';
import { OfferFragment, PromocodesOfferType } from '@occc/graphql';
import { isFieldDirty } from '@occc-app/offerHelpers/utils';
import {
  makeEarningMechanismCreateOperation,
  makeEarningMechanismUpdateOperation,
} from '@occc-app/offerHelpers/operations/earningMechanism';
import { earningMechanismInputCreator } from '@occc-app/offerHelpers/earningMechanismInputCreator';
import { earningMechanismHelper } from '@occc-app/offerHelpers/utils/earningMechanismHelper';
import { getOfferNameAndDescriptionUpdateOperations } from '@occc-app/routes/Offer/components/OfferConfigureForm/utils/offerNameAndDescriptionUpdateOperations';
import { getLimitsInConstraintOperations, LimitType } from '@occc/feat-limits';
import {
  getAwardRestrictionsOperations,
  getFulfillmentWindowOperations,
} from '@occc/feat-constraints';

import {
  PromocodesConfigureFormValues,
  PromocodesFormValues,
} from './promocodes-configuration.types';

const preparePromocodesAwardFormValue = (
  values: PromocodesFormValues,
): AwardPointsOrVouchersInput | null => {
  if (values.awardType === AwardType.AwardPoints) {
    return { awardPoints: values.awardPoints };
  }

  if (values.awardType === AwardType.AwardVouchers) {
    const { awardVouchers } = values;
    const voucheredOfferId = awardVouchers?.voucheredOffer?.id;

    if (!awardVouchers || !voucheredOfferId) {
      return null;
    }

    return {
      awardVouchers: {
        voucheredOfferId,
        voucherCount: 1,
        voucherExpirationDays:
          awardVouchers.isEnabled && awardVouchers.voucherExpirationDays
            ? awardVouchers.voucherExpirationDays
            : null,
      },
    };
  }

  return null;
};

const getCodesTypeFromOffer = (offer: OfferFragment | undefined): PromocodesOfferType => {
  if (earningMechanismHelper.isPromocodes(offer?.earningMechanism)) {
    return offer.earningMechanism.codesType;
  }

  return PromocodesOfferType.Global;
};

const prepareConfigurePromocodesAwardConfiguration = (
  offer: OfferFragment | undefined,
  promoCodesFormValues: PromocodesFormValues,
) => {
  const award = preparePromocodesAwardFormValue(promoCodesFormValues);
  return earningMechanismInputCreator.createPromocodes(award, getCodesTypeFromOffer(offer));
};

export const getEarningMechanismOperations = (
  offer: OfferFragment | undefined,
  values: PromocodesConfigureFormValues,
  dirtyFields: DirtyFieldsType,
): Operation[] => {
  const operations: Operation[] = [];

  if (!offer) {
    return operations;
  }

  const earningMechanism = offer.earningMechanism;
  const configuration = prepareConfigurePromocodesAwardConfiguration(offer, values.promocodes);

  if (earningMechanism?.__typename === 'EarningMechanismPromocodes' && earningMechanism.id) {
    if (isFieldDirty<PromocodesConfigureFormValues>(dirtyFields, 'promocodes')) {
      operations.push(makeEarningMechanismUpdateOperation(earningMechanism.id, configuration));
    }
    return operations;
  }

  operations.push(makeEarningMechanismCreateOperation(offer.id, configuration));

  return operations;
};

export const getConfigureFormOperations: GetConfigureFormOperations<
  PromocodesConfigureFormValues
> = (offerId, offer, values, dirtyFields) => {
  const earningMechanismOperations = getEarningMechanismOperations(offer, values, dirtyFields);

  const fulfillmentWindowOperations = getFulfillmentWindowOperations(
    offerId,
    values.fulfillmentWindow,
    dirtyFields,
  );

  const awardCountLimitsOperations = getLimitsInConstraintOperations(
    offerId,
    values.awardCount,
    dirtyFields,
    LimitType.AwardCount,
  );

  const awardRestrictionsOperations = offer
    ? getAwardRestrictionsOperations({
        offer,
        values: values.awardRestrictions,
        dirtyFields,
        forceDelete: values.promocodes.awardType !== AwardType.AwardVouchers,
      })
    : [];

  const additionalOperations: Operation[] = [];

  additionalOperations.push(
    ...getOfferNameAndDescriptionUpdateOperations(offerId, values, dirtyFields),
  );

  const customDataOperation = getOfferCustomDataOperation({
    dirtyFields,
    values,
    offerId,
  });

  if (customDataOperation) {
    additionalOperations.push(customDataOperation);
  }

  return [
    ...earningMechanismOperations,
    ...fulfillmentWindowOperations,
    ...awardCountLimitsOperations,
    ...awardRestrictionsOperations,
    ...additionalOperations,
  ];
};
